import SidebarFloatingAd from 'components/ad/SidebarFloatingAd'
import NC from 'nc/NC'
import React, { useMemo } from 'react'
import { useAffilate } from 'shared-components/hooks/use-affilate'
import { useTaglessAd } from 'shared-components/hooks/use-tagless-ad'
import ArticleConversation from 'shared-components/comments/conversation/ArticleConversation'
import { conversationSlot } from 'shared-components/comments/conversation/conversationSlot'
import ConversationRailCard from 'shared-components/comments/conversation/ConversationRailCard'
import PostNavigation from 'shared-components/layout/PostNavigation'
import ScrollEventsListener from 'shared-components/service/ScrollEventsListener'
import styleUtils from 'shared-components/styles/util.module.css'
import AdaptiveThreeColumnLayout from 'shared-components/ui/AdaptiveThreeColumnLayout'
import BreadCrumbs from 'shared-components/ui/BreadCrumbs'
import Disclaimer from 'shared-components/ui/Disclaimer'
import GoogleButtons from 'shared-components/ui/GoogleButtons'
import PostShortLine from 'shared-components/ui/PostShortLine'
import PostTags from 'shared-components/ui/PostTags'
import PostTitle from 'shared-components/ui/PostTitle'
import SidedCategoryMenu from 'shared-components/ui/SidedCategoryMenu'
import SourcesLine from 'shared-components/ui/SourcesLine'
import SplitterLine from 'shared-components/ui/SplitterLine'
import { DSPostPageSG } from 'types.sg.ds'
import stylesCommon from './PostTemplate.ReviewTemplate.module.css'
import styles from './PostTemplate.module.css'
import { useArticleViewed } from 'shared-components/hooks/use-article-viewed'
import UnderTitleAds from 'components/ad/UnderTitleAds'
import { GuideOwnersProvider } from 'components/owner-notes/guide/GuideOwnersProvider'
import LabSurfaces, { useLabSurfacesDocked } from 'components/product/LabSurfaces'
import { CatalogAnalyticsProvider, type CatalogAnalyticsScope } from 'lib/analytics/catalog'
import { useDeclaredState } from 'lib/owner-notes/declared'

interface PostTemplateProps {
  page: DSPostPageSG
}

const PostBody: React.VFC<PostTemplateProps> = ({ page }) => {
  const [first, ...blocks] = page.blocks
  // The end-of-article conversation follows "Have your say" (nc-have-your-say) when the post
  // has it (Guillaume, 2026-10-27) — else after the tags, as before.
  const conversationAt = conversationSlot(blocks)
  useAffilate(page.productBars)
  useTaglessAd()
  // The mobile conversation bar yields while the LabCompare drawer / pill is docked (a pin, a
  // focus or a declared product) — not on every page with a strip.
  const labDocked = useLabSurfacesDocked()
  const conversation =
    page.pagePath && page.meta.engagementContainerId ? (
      <ArticleConversation className={stylesCommon.comments} hideBar={labDocked} />
    ) : null

  return (
    <>
      <ScrollEventsListener />
      <Disclaimer />
      <div
        className={styleUtils.wrapper}
        ref={useArticleViewed({
          articleId: page.meta?.postId?.toString() ?? null,
          title: page.title,
          url: page.pagePath,
          categoryLevel: 1,
          categoryTitle: page.meta.categories[0]?.label ?? '',
          author: page.authors.authoredBy.map(author => author.name).join(', '),
          wordCount: parseInt(page.meta.dataLayer?.word_count?.toString() ?? '0', 10),
          date: page.meta.dataLayer?.original_pub_date?.toString() ?? '',
        })}
      >
        <BreadCrumbs list={page.breadcrumbs} className={stylesCommon.breadCrumbs} />
        <PostTitle title={page.title} subtitle={page.subtitle} />
        <PostShortLine post={page} className={styles.shortLine} />
        <UnderTitleAds ads={page.underTitleAds} />
        <SplitterLine className={styles.splitter} />
        <AdaptiveThreeColumnLayout
          left={
            page.sideMenu.length ? (
              <SidedCategoryMenu
                className={stylesCommon.sidebarPostNav}
                title="Go deeper"
                menu={page.sideMenu}
              />
            ) : null
          }
          className={styles.content}
          content={
            <>
              <NC data={first} tpl="post" index={0} />

              <GoogleButtons data={page.googleButtons} />

              {blocks.map((item, index) => (
                <React.Fragment key={index + 1}>
                  <NC data={item} tpl="text" index={index} />
                  {index === conversationAt ? conversation : null}
                </React.Fragment>
              ))}

              {page.sources.length ? (
                <SourcesLine className={styleUtils.nc} sources={page.sources} />
              ) : null}

              <PostTags meta={page.meta} className={stylesCommon.postTags} />

              {conversationAt === -1 ? conversation : null}
            </>
          }
          sidebar={
            <PostNavigation>
              {[...page.sidebarContent, ...page.sidebarAds].map((item, index) => (
                <NC key={index} data={item} tpl="sidebar" index={index} />
              ))}
              {/* After every WordPress sidebar widget (the trust box arrives in sidebarAds as an
                  nc-multi), before the sticky Playwire ads: the widgets scroll away above the card and
                  never pass under the stuck strip; the ads still pin under it. */}
              <ConversationRailCard />
              <SidebarFloatingAd />
            </PostNavigation>
          }
        />
        {/* <RecommendSG
          containerClassName={stylesCommon.related}
          itemClassName={styleRelatedCard.relatedCard}
          title="More from SoundGuys"
        /> */}
      </div>
    </>
  )
}

/**
 * A post whose `nc-deals-large` card carries the product's strip (`page.guideOwners`, set by
 * `dealhuntModifyPost` when DealHunt resolved an associated product on a card — 2026-10-21)
 * mounts what the strip reads, as the best-list templates do: the `post_page_*` analytics scope,
 * the one declared product (#63) and the lab surfaces (the side panel / dialog mount on a pin
 * or a focus, never with the page). A post without a strip mounts none of it and renders as
 * before.
 */
const PostTemplate: React.VFC<PostTemplateProps> = props => {
  const { page } = props
  const scope = useMemo<CatalogAnalyticsScope>(
    () => ({
      page_type: 'post_page',
      ...(page.meta.categories[0]?.label ? { category: page.meta.categories[0].label } : {}),
    }),
    [page.meta.categories]
  )
  const declared = useDeclaredState()
  if (!page.guideOwners) {
    return <PostBody {...props} />
  }
  return (
    <CatalogAnalyticsProvider value={scope}>
      <GuideOwnersProvider page={page.guideOwners}>
        <LabSurfaces declared={declared} mountOnPin>
          <PostBody {...props} />
        </LabSurfaces>
      </GuideOwnersProvider>
    </CatalogAnalyticsProvider>
  )
}

export default PostTemplate
